import * as Sentry from '@sentry/react-native';
import { usePostHog } from 'posthog-js/react';
import React from 'react';
import { Platform, StyleSheet, Text, View } from 'react-native';
import { fontFamilies } from '../utils/fontLoader';
import logger from '../utils/logger';
import { responsiveFontSize, responsiveSpacing } from '../utils/responsiveHelpers';
import { GradientButton } from './GradientComponents';

// Detects chunk/lazy-bundle load failures across bundlers and browsers.
// Metro on web throws plain `TypeError: Failed to fetch`; webpack throws
// `ChunkLoadError`; Safari throws "Importing a module script failed";
// Firefox throws "error loading dynamically imported module".
function isChunkLoadError(error, errorInfo) {
  if (!error) return false;
  if (error.name === 'AsyncRequireError' || error.name === 'ChunkLoadError') return true;
  const msg = String(error.message || '');
  if (/Loading chunk \d+ failed/i.test(msg)) return true;
  if (/Importing a module script failed/i.test(msg)) return true;
  if (/error loading dynamically imported module/i.test(msg)) return true;
  // Metro: TypeError: Failed to fetch from fetchThenEvalAsync / asyncRequire / loadBundleAsync
  if (/Failed to fetch|Load failed|NetworkError when attempting to fetch resource/i.test(msg)) {
    const stack = String(error.stack || '') + String(errorInfo?.componentStack || '');
    if (/asyncRequire|fetchThenEvalAsync|loadBundleAsync|fetchAsync/.test(stack)) return true;
  }
  return false;
}

/**
 * ErrorFallbackUI Component
 * User-friendly error screen shown when an error boundary catches an error
 */
const ErrorFallbackUI = ({ error, resetError, fallbackMessage }) => {
  return (
    <View style={styles.errorContainer}>
      <View style={styles.errorCard}>
        <Text style={styles.errorTitle}>Oops! Something went wrong</Text>
        <Text style={styles.errorMessage}>
          {fallbackMessage ||
            "We encountered an unexpected error. Our team has been notified and we're working on a fix."}
        </Text>

        {__DEV__ && error && (
          <View style={styles.errorDetailsContainer}>
            <Text style={styles.errorDetailsTitle}>Error Details (Dev Only):</Text>
            <Text style={styles.errorDetailsText}>{error.toString()}</Text>
          </View>
        )}

        <GradientButton
          onPress={resetError}
          text="Try Again"
          style={styles.resetButton}
          accessibilityLabel="Try again button"
          accessibilityHint="Attempts to recover from the error"
        />
      </View>
    </View>
  );
};

/**
 * PostHogLogger Component
 * Functional component wrapper to access PostHog hook
 */
class ErrorBoundaryCore extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error, errorInfo) {
    // Stale chunk after deployment / Metro restart: auto-reload to fetch fresh bundles.
    // Metro surfaces lazy-bundle failures as plain `TypeError: Failed to fetch` from
    // `fetchThenEvalAsync`/`asyncRequire`, not as `AsyncRequireError` — match both.
    if (
      Platform.OS === 'web' &&
      isChunkLoadError(error, errorInfo) &&
      !sessionStorage.getItem('chunk_reload')
    ) {
      sessionStorage.setItem('chunk_reload', '1');
      window.location.reload();
      return;
    }

    // Log error details
    logger.error('[ErrorBoundary] Caught error:', {
      error: error.toString(),
      componentStack: errorInfo.componentStack,
    });

    // Capture to Sentry with component stack trace
    Sentry.captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      level: 'error',
      tags: {
        errorBoundary: true,
      },
    });

    // Log to PostHog via the provided callback
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  resetError = () => {
    this.setState({
      hasError: false,
      error: null,
    });
  };

  componentDidMount() {
    // Clear the reload guard after a delay — if the app renders successfully
    // for 5 seconds, the stale chunk is resolved. Clearing immediately would
    // allow an infinite reload loop if CDN still serves the stale chunk.
    if (Platform.OS === 'web' && sessionStorage.getItem('chunk_reload')) {
      this._chunkGuardTimer = setTimeout(() => {
        sessionStorage.removeItem('chunk_reload');
      }, 5000);
    }

    // Catch chunk-load failures whose promise rejection escapes React's tree
    // (e.g. during initial route mount, before any Suspense boundary is in place).
    if (Platform.OS === 'web' && typeof window !== 'undefined') {
      this._onUnhandledRejection = (event) => {
        const reason = event?.reason;
        if (
          isChunkLoadError(reason) &&
          !sessionStorage.getItem('chunk_reload')
        ) {
          sessionStorage.setItem('chunk_reload', '1');
          window.location.reload();
        }
      };
      window.addEventListener('unhandledrejection', this._onUnhandledRejection);
    }
  }

  componentWillUnmount() {
    if (this._chunkGuardTimer) {
      clearTimeout(this._chunkGuardTimer);
    }
    if (Platform.OS === 'web' && this._onUnhandledRejection) {
      window.removeEventListener('unhandledrejection', this._onUnhandledRejection);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallbackUI
          error={this.state.error}
          resetError={this.resetError}
          fallbackMessage={this.props.fallbackMessage}
        />
      );
    }

    return this.props.children;
  }
}

/**
 * ErrorBoundary Component
 * Wraps the error boundary with PostHog integration
 */
const ErrorBoundary = ({ children, fallbackMessage }) => {
  const posthog = usePostHog();

  const handleError = (error, errorInfo) => {
    // Log error to PostHog for cross-referencing with Sentry
    if (posthog) {
      posthog.capture('error_boundary_triggered', {
        error: error.toString(),
        errorMessage: error.message,
        errorStack: error.stack,
        componentStack: errorInfo.componentStack,
        platform: Platform.OS,
        // Cross-reference with Sentry event ID if available
        sentryEventId: Sentry.lastEventId(),
      });
    }
  };

  return (
    <ErrorBoundaryCore onError={handleError} fallbackMessage={fallbackMessage}>
      {children}
    </ErrorBoundaryCore>
  );
};

/**
 * Wrap with Sentry for enhanced error tracking
 */
export default Sentry.wrap(ErrorBoundary);

const styles = StyleSheet.create({
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F5F5F5',
    padding: responsiveSpacing(24),
  },
  errorCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: 16,
    ...Platform.select({
      web: {
        boxShadow: '0px 0px 16px rgba(0, 0, 0, 0.12)',
      },
      default: {
        boxShadow: {
          offsetX: 0,
          offsetY: 0,
          blurRadius: 16,
          spreadDistance: 0,
          color: 'rgba(0, 0, 0, 0.12)',
        },
        elevation: 8,
      },
    }),
    width: '100%',
    maxWidth: responsiveSpacing(600),
    alignItems: 'center',
    paddingTop: responsiveSpacing(48),
    paddingHorizontal: responsiveSpacing(24),
    paddingBottom: responsiveSpacing(32),
  },
  errorTitle: {
    fontFamily: fontFamilies.satoshi.bold,
    fontWeight: '700',
    fontSize: responsiveFontSize(28),
    lineHeight: responsiveFontSize(28) * 1.2,
    letterSpacing: responsiveFontSize(28) * -0.01,
    color: '#0E0E0E',
    textAlign: 'center',
    marginBottom: responsiveSpacing(16),
  },
  errorMessage: {
    fontFamily: fontFamilies.satoshi.regular,
    fontWeight: '400',
    fontSize: responsiveFontSize(16),
    lineHeight: responsiveFontSize(16) * 1.6,
    color: 'rgba(14, 14, 14, 0.8)',
    textAlign: 'center',
    marginBottom: responsiveSpacing(32),
    maxWidth: '90%',
  },
  errorDetailsContainer: {
    backgroundColor: '#FFF5F5',
    borderRadius: 8,
    padding: responsiveSpacing(16),
    marginBottom: responsiveSpacing(24),
    width: '100%',
    borderWidth: 1,
    borderColor: '#FFEBEE',
  },
  errorDetailsTitle: {
    fontFamily: fontFamilies.satoshi.bold,
    fontWeight: '700',
    fontSize: responsiveFontSize(14),
    color: '#D32F2F',
    marginBottom: responsiveSpacing(8),
  },
  errorDetailsText: {
    fontFamily: fontFamilies.satoshi.regular,
    fontWeight: '400',
    fontSize: responsiveFontSize(12),
    lineHeight: responsiveFontSize(12) * 1.4,
    color: '#D32F2F',
  },
  resetButton: {
    marginTop: responsiveSpacing(8),
  },
});
